// ══════════════════════════════════════════════════════════════════════════════
// PAGE DOCUMENTS PRO - Google Drive connecté
// AbracadaWorld - Cockpit Pro (Artisans/Vendeurs)
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FolderOpen, File, FileText, Image, Film, Music, Archive,
  RefreshCw, Search, Grid, List, ExternalLink, Download,
  ChevronRight, Clock, HardDrive, LogOut, Folder, X,
  FileSpreadsheet, Presentation, Code
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getToken = () => {
  return localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
};

const getFileIcon = (mimeType) => {
  if (!mimeType) return File;
  if (mimeType.includes('folder')) return Folder;
  if (mimeType.includes('document') || mimeType.includes('text')) return FileText;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return FileSpreadsheet;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return Presentation;
  if (mimeType.includes('image')) return Image;
  if (mimeType.includes('video')) return Film;
  if (mimeType.includes('audio')) return Music;
  if (mimeType.includes('zip') || mimeType.includes('archive')) return Archive;
  if (mimeType.includes('code') || mimeType.includes('javascript')) return Code;
  return File;
};

const getFileColor = (mimeType) => {
  if (!mimeType) return 'text-gray-500';
  if (mimeType.includes('folder')) return 'text-yellow-500';
  if (mimeType.includes('document')) return 'text-blue-500';
  if (mimeType.includes('spreadsheet')) return 'text-green-500';
  if (mimeType.includes('presentation')) return 'text-orange-500';
  if (mimeType.includes('image')) return 'text-pink-500';
  if (mimeType.includes('video')) return 'text-purple-500';
  if (mimeType.includes('audio')) return 'text-indigo-500';
  return 'text-gray-500';
};

export default function DocumentsProPage() {
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [selectedFile, setSelectedFile] = useState(null);

  const checkGoogleStatus = async () => {
    try {
      const token = getToken();
      if (!token) {
        setGoogleStatus({ connected: false, loading: false });
        return;
      }
      const res = await fetch(`${API_URL}/api/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGoogleStatus({ ...data, loading: false });
        if (data.connected) {
          loadFiles();
        }
      } else {
        setGoogleStatus({ connected: false, loading: false });
      }
    } catch (error) {
      setGoogleStatus({ connected: false, loading: false });
    }
  };

  const connectGoogle = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const { auth_url } = await res.json();
        window.location.href = auth_url;
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Déconnecter Google ?')) return;
    
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/google/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleStatus({ connected: false, loading: false });
      setFiles([]);
      toast.success('Déconnecté');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const loadFiles = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/drive/files?page_size=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setFiles(data.files || []);
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkGoogleStatus();
    
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      toast.success('Google Drive connecté !');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    const sizes = ['o', 'Ko', 'Mo', 'Go'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const filteredFiles = files.filter(file => {
    if (!searchQuery) return true;
    return file.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (!googleStatus.loading && !googleStatus.connected) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-lg p-8 text-center">
          {/* Badge Beta */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold mb-4">
            <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
            Version Beta
          </div>
          
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#1a2a4a] to-[#2d4a6a] flex items-center justify-center">
            <HardDrive size={36} className="text-white" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Mes Documents Pro
          </h1>
          <p className="text-gray-500 mb-6">
            Connecte ton cloud pour accéder à tes devis, factures et documents professionnels.
          </p>
          
          {/* Google Drive - Disponible */}
          <button
            onClick={connectGoogle}
            data-testid="connect-drive-pro-btn"
            className="w-full py-4 bg-white border-2 border-gray-200 rounded-2xl flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors mb-3"
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-6 h-6" />
            <span className="font-semibold text-gray-700">Google Drive</span>
            <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Disponible</span>
          </button>
          
          {/* OneDrive - Prochainement */}
          <div className="w-full py-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center gap-3 mb-3 opacity-60">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7v10l10 5 10-5V7L12 2z" fill="#0078D4"/>
              <path d="M12 2v20M2 7l10 5 10-5" stroke="#fff" strokeWidth="1"/>
            </svg>
            <span className="font-semibold text-gray-500">Microsoft OneDrive</span>
            <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Prochainement</span>
          </div>
          
          {/* iCloud Drive - Prochainement */}
          <div className="w-full py-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center gap-3 opacity-60">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M19.5 10.5c0-4.14-3.36-7.5-7.5-7.5S4.5 6.36 4.5 10.5c0 .17.01.34.02.5A5.5 5.5 0 006 21.5h12a5.5 5.5 0 001.5-10.8c0-.07 0-.13 0-.2z" fill="#007AFF"/>
            </svg>
            <span className="font-semibold text-gray-500">iCloud Drive</span>
            <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Prochainement</span>
          </div>
          
          <p className="mt-6 text-xs text-gray-400">
            Tes données restent privées et sécurisées.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Pro */}
      <div 
        className="sticky top-0 z-20 px-4 py-4"
        style={{ 
          background: 'linear-gradient(135deg, #1a2a4a 0%, #2d4a6a 100%)',
          paddingTop: 'max(16px, env(safe-area-inset-top))'
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-white">Mes Documents</h1>
            {googleStatus.email && (
              <p className="text-white/60 text-sm">{googleStatus.email}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
            >
              {viewMode === 'list' ? <Grid size={18} className="text-white" /> : <List size={18} className="text-white" />}
            </button>
            <button
              onClick={loadFiles}
              disabled={loading}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
            >
              <RefreshCw size={18} className={`text-white ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={disconnectGoogle} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <LogOut size={18} className="text-white/70" />
            </button>
          </div>
        </div>

        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pl-12 pr-4 py-3 bg-white rounded-xl"
          />
        </div>
      </div>

      {/* Liste */}
      <div className="p-4 pb-32">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#1a2a4a] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FolderOpen size={48} className="mx-auto text-gray-300 mb-3" />
            <p>Aucun fichier</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
            {filteredFiles.map((file, index) => {
              const FileIcon = getFileIcon(file.mimeType);
              const iconColor = getFileColor(file.mimeType);
              
              return (
                <div
                  key={file.id}
                  onClick={() => setSelectedFile(file)}
                  className={`flex items-center gap-4 px-4 py-3.5 cursor-pointer hover:bg-gray-50 ${
                    index < filteredFiles.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  {file.thumbnailLink ? (
                    <img src={file.thumbnailLink} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className={`w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center ${iconColor}`}>
                      <FileIcon size={20} />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{file.name}</p>
                    <p className="text-xs text-gray-400">{formatSize(file.size)} · {formatDate(file.modifiedTime)}</p>
                  </div>
                  
                  <ChevronRight size={18} className="text-gray-300" />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredFiles.map((file) => {
              const FileIcon = getFileIcon(file.mimeType);
              const iconColor = getFileColor(file.mimeType);
              
              return (
                <div
                  key={file.id}
                  onClick={() => setSelectedFile(file)}
                  className="bg-white rounded-2xl p-4 shadow-sm cursor-pointer hover:shadow-md"
                >
                  {file.thumbnailLink ? (
                    <img src={file.thumbnailLink} alt="" className="w-full h-24 rounded-xl object-cover mb-3" />
                  ) : (
                    <div className={`w-full h-24 rounded-xl bg-gray-100 flex items-center justify-center mb-3 ${iconColor}`}>
                      <FileIcon size={36} />
                    </div>
                  )}
                  
                  <p className="font-medium text-gray-800 truncate text-sm">{file.name}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(file.modifiedTime)}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal détail */}
      {createPortal(
        <AnimatePresence>
          {selectedFile && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 z-[10002]"
                onClick={() => setSelectedFile(null)}
              />
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 100 }}
                className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[10002]"
              style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    {selectedFile.thumbnailLink ? (
                      <img src={selectedFile.thumbnailLink} alt="" className="w-14 h-14 rounded-xl object-cover" />
                    ) : (
                      <div className={`w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center ${getFileColor(selectedFile.mimeType)}`}>
                        {React.createElement(getFileIcon(selectedFile.mimeType), { size: 28 })}
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-gray-800 line-clamp-2">{selectedFile.name}</h3>
                      <p className="text-sm text-gray-500">{formatSize(selectedFile.size)} · {formatDate(selectedFile.modifiedTime)}</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedFile(null)} className="p-2 hover:bg-gray-100 rounded-full">
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-3">
                  {selectedFile.webViewLink && (
                    <a
                      href={selectedFile.webViewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-3.5 bg-[#1a2a4a] text-white rounded-2xl font-semibold flex items-center justify-center gap-2 hover:bg-[#2d4a6a]"
                    >
                      <ExternalLink size={20} />
                      Ouvrir dans Drive
                    </a>
                  )}
                  
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="w-full py-3.5 border-2 border-gray-200 text-gray-700 rounded-2xl font-medium hover:bg-gray-50"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
